import { useCallback } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { Outlet, useSearchParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { UnifiedSearchModal } from 'components/UnifiedSearch'
import {
  LeftNavBar,
  SlideOutPanel,
  useLeftNavStore,
} from 'components/Layout/LeftNav'
import { Header } from 'components/Layout/Header'
import { NewFooter } from 'components/Layout/Footer/NewFooter'
import { AuthenticatedNav } from 'components/Layout/Navbar'
import Footer from 'components/Layout/Footer/Footer'
import { featureFlagService } from 'utils/featureFlagService'

export function AppLayout() {
  const { enableNewNavigation } = featureFlagService()

  // Render separate components to avoid hook issues when feature flag is off
  if (enableNewNavigation) {
    return <NewAppLayout />
  }

  return <OldAppLayout />
}

// OLD NAVIGATION LAYOUT (when feature flag is disabled)
function OldAppLayout() {
  const { isAuthenticated } = useAuth()

  return (
    <OldLayoutRoot>
      {isAuthenticated && <AuthenticatedNav />}
      <OldContent id="app-content-container">
        <Outlet />
      </OldContent>
      <Footer />
      {isAuthenticated && <UnifiedSearchModal />}
    </OldLayoutRoot>
  )
}

// NEW NAVIGATION LAYOUT (when feature flag is enabled)
function NewAppLayout() {
  const { isAuthenticated } = useAuth()
  const { isCollapsed, isSlideOutOpen, activeSection } = useLeftNavStore()
  const [, setSearchParams] = useSearchParams()

  const openSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('search', 'true')
      return prev
    })
  }, [setSearchParams])

  const showSlideOut = isAuthenticated && isSlideOutOpen && activeSection

  return (
    <LayoutRoot data-new-nav="true">
      {/* Top Header - spans full width */}
      {isAuthenticated && <Header onSearchClick={openSearch} />}

      {/* Main Area: Left Nav + Content */}
      <MainArea>
        {/* Left Navigation Bar */}
        {isAuthenticated && <LeftNavBar />}

        {/* App Shell with border effect */}
        <AppShell $hasLeftNav={isAuthenticated && !isCollapsed}>
          {/* Slide-Out Panel - inside AppShell, pushes content */}
          {isAuthenticated && <SlideOutPanel $isOpen={!!showSlideOut} />}

          <ContentArea>
            <ContentContainer id="app-content-container">
              <Outlet />
            </ContentContainer>
            <NewFooter />
          </ContentArea>
        </AppShell>
      </MainArea>

      {isAuthenticated && <UnifiedSearchModal />}
    </LayoutRoot>
  )
}

const LayoutRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: var(--nav-bg-primary);
`

const MainArea = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const AppShell = styled.div<{ $hasLeftNav: boolean }>`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
  margin: var(--app-border-width);
  margin-left: ${({ $hasLeftNav }) =>
    $hasLeftNav ? '0' : 'var(--app-border-width)'};
  background: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: var(--page-container-vertical-padding);
  padding-bottom: var(--page-container-bottom-padding);
`

// OLD LAYOUT STYLED COMPONENTS (when feature flag is disabled)
const OldLayoutRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
`

const OldContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`
